import { logger } from '@belong/logging/logger';
import { ISupportCategory } from '@belong/types/support';
import { withBasePath } from '@belong/url-utils';
import ContentAPI from 'src/services/ContentAPI';
import ContentGQL from 'src/services/ContentGQL';
import { FSupportArticle, IArticle } from 'src/models/articles';
import CategoryModel from 'src/models/Categories';
import { Entry } from 'contentful';
import { IPageError } from 'src/core/types';

interface IArticlePageData {
  article: IArticle;
  relatedArticles: IArticle[];
}

// Add local link to article category
export const injectCategoryLink = (article: IArticle, parentCategory: ISupportCategory): any => ({
  ...article,
  header: {
    ...article.header,
    autoCategoryLink: {
      label: parentCategory.label,
      variant: 'quarternary',
      a11yLabel: parentCategory.label,
      href: withBasePath(`${parentCategory.path}`)
    }
  }
});

const loadArticlePageData = async (
  categoryModel: CategoryModel,
  category: ISupportCategory,
  articleSlug: string
): Promise<IArticlePageData | IPageError> => {
  let articleData;
  try {
    [articleData] = await ContentAPI.getArticle(category.id, articleSlug);
    if (!articleData) {
      throw new Error();
    }
  } catch (err) {
    logger.error(`Could not find article for '${articleSlug}' in category '${category.id}' (${category.label})`);
    return { statusCode: 404 };
  }

  let article;
  try {
    article = injectCategoryLink(FSupportArticle(articleData, categoryModel), category);
  } catch (err) {
    logger.error('Error transforming article data:', err.message);
    return { statusCode: 500 };
  }

  // Also ensure the current article doesn't appear in the related articles list
  const excludeCurrentArticle = (entry: Entry<any>): boolean => entry.sys.id !== article.id;

  // Load related articles (3x articles from same category, ordered by promoted, title)
  let relatedArticles;
  try {
    relatedArticles = (await ContentGQL.getArticlesByCategory([category.id], 4))
      .filter(excludeCurrentArticle)
      .map(item => FSupportArticle(item, categoryModel))
      .map(item => ({
        ...item,
        categories: item.categories.map(catId => categoryModel.getCategoryById(catId.toString()))
      }))
      .slice(0, 3);
  } catch (err) {
    logger.error(`Could not load related articles in category '${category.id}' (${category.label})`, err.message);
    return { statusCode: 500 };
  }

  return {
    article,
    relatedArticles
  };
};

export default loadArticlePageData;
